import ButtonPopup from "../buttons/ButtonPopup.tsx";
import { Bars3Icon } from "@heroicons/react/24/outline";
import Popup, { IPopupRef } from "./Popup.tsx";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { IPlayerChannel } from "../../interfaces/IPlayerUIState.ts";
import { PopupItemButton } from "./PopupItemButton.tsx";
import { changeVideoAsync } from "../player/playerSlice.ts";
import { useAppDispatch, useAppSelector } from "../../stores/store.ts";
import { LazyLoadImage } from "react-lazy-load-image-component";
import UtilsHelper from "../../helpers/utilsHelper.ts";
import { usePlayerPage } from "../../hooks/usePlayerPage.tsx";
import { playerUIActions } from "../player/playerUISlice.ts";
import PlayerUIHelpers from "../../helpers/playerUIHelpers.ts";
import DateHelper from "../../helpers/dateHelper.ts";

interface IProps {
    isOpen: boolean,
    setIsOpen: (isOpen: boolean) => void,
    channels: IPlayerChannel[]
}

const PlayerChangeChannelListPopup = forwardRef<{ closePopup: () => void }, IProps>(({ isOpen, setIsOpen, channels }, ref) => {
    const { setMenuVisible } = usePlayerPage();
    const dispatch = useAppDispatch();
    const actualChannelIndex = useAppSelector(store => store.playerUI.actualChannelIndex);
    const popupRef = useRef<IPopupRef>(null);

    const closePopup = () => {
        if (isOpen && popupRef.current) {
            popupRef.current.closePopup();
        }
    }

    const togglePopup = () => {
        if (isOpen) closePopup();
        else
            setIsOpen(true);
    }

    const handleButtonOnClick = () => {
        togglePopup();
    }

    const handleChangeItemClick = (channel: IPlayerChannel, channelIndex: number) => {
        dispatch(playerUIActions.setActualChannelIndex(channelIndex))
        dispatch(changeVideoAsync({ chId: channel.ch_id }));
        closePopup();
    }

    useEffect(() => {
        const elem = document.getElementById(`channel_item_${actualChannelIndex}`);
        if (elem) {
            elem.scrollIntoView({ inline: "start", behavior: "smooth" });
        }
    }, [actualChannelIndex]);

    useImperativeHandle(ref, () => ({
        closePopup
    }));
    return (
        <div className={"relative hidden md:block show-element-animation-ease-in-out "}>
            <ButtonPopup
                isOpen={isOpen}
                onClick={handleButtonOnClick}
                className={"bg-transparent py-0 px-0 flex items-center"}
            >
                <Bars3Icon className={`w-7 h-7 stroke-[2.5] transition-colors text-neutral-200 hover:text-white ${isOpen && "text-white"}`} />
            </ButtonPopup>
            <Popup
                className={"-left-6 z-10 shadow-3xl"}
                ref={popupRef}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                title={"Wybierz kanał"}
                handleAnimationEnd={() => { }}
            >
                <div className={"overflow-y-scroll w-[320px] sm:w-[370px] md:w-[420px] 2xl:w-[400px] h-[50vh]"}
                    onScroll={setMenuVisible}>
                    {channels.map((channel, index) => {
                        const actualEpgItem = PlayerUIHelpers.getActualEpgItem(channel);
                        return <PopupItemButton
                            id={`channel_item_${index}`}
                            key={channel.ch_id}
                            disabled={index === actualChannelIndex}
                            handleClick={() => { handleChangeItemClick(channel, index) }}
                            className={"p-0"}
                        >
                            <div className={"flex items-center space-x-2 w-full border-b border-neutral-300 py-3 px-2"}>
                                <LazyLoadImage
                                    alt=""
                                    effect="opacity"
                                    className="grow align-middle"
                                    wrapperClassName={`flex items-center w-10 shrink-0`}
                                    src={`${import.meta.env.VITE_MIX_CHANNEL_LOGO_DARK_URL}/${channel.logo}`}
                                />
                                <div className={"flex flex-col items-start w-[calc(100%-50px)]"}>
                                    <p className={"font-medium text-neutral-500"}>{channel.name}</p>
                                    {actualEpgItem &&
                                        <>
                                            <p className={"text-sm text-neutral-700 font-semibold truncate w-full text-left"}>{actualEpgItem.name}</p>
                                            <p className={"text-sm text-neutral-500"}>{`${DateHelper.GetHourAndMinuteFromUnixTimespanDate(actualEpgItem.start_timestamp)} - ${DateHelper.GetHourAndMinuteFromUnixTimespanDate(actualEpgItem.stop_timestamp)}`} </p>
                                            <div className="mt-0.5 flex-start flex h-1 w-full overflow-hidden bg-neutral-300 font-sans text-xs font-medium rounded-full">
                                                <div
                                                    className={`flex h-full items-center justify-center overflow-hidden break-all bg-[#008DD2] bg-gradient-to-b from-sky-500 to-sky-900 text-white rounded-full`}
                                                    style={{ width: UtilsHelper.calcProgressBar(actualEpgItem) }}
                                                />
                                            </div>
                                        </>
                                    }
                                </div>
                            </div>
                        </PopupItemButton>
                    })}
                </div>
            </Popup>
        </div>
    )
}
)

export default PlayerChangeChannelListPopup;